/**
 * HM-386..388 + F2-d cluster login PIN multi-hotel: orchestratore LoginPage.
 *
 * Quattro step:
 *  0. hotel  - scelta hotel (HotelSelectStep, F2-d). Click -> step 'select'.
 *  1. select - griglia operatori del tenant scelto (OperatorsGrid). Click -> 'pin'.
 *  2. pin    - keypad + dots (PinKeypadStep, HM-387) con hotelId. Success -> 'success'.
 *  3. success - conferma + auto-redirect a `from` query param (LoginSuccess, HM-388).
 *
 * Il gestionale online (api-edge) e' multi-tenant: l'hotelId scelto viene
 * passato a `/auth/operators` e `/auth/login-pin` per scopare per tenant.
 *
 * `from` query param risolve la route originale che ha redirectato l'utente
 * verso /login (vedi RequireAuth HM-385).
 *
 * Fallback admin email+password vive su /login-admin (LoginAdminPage). Link in
 * footer per accesso a quel flow.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { Operator } from '../../api/auth.js';
import type { HotelOption } from '../../config/hotels.js';
import { type AuthUser, useAuth } from '../../stores/auth.js';
import { HotelSelectStep } from './HotelSelectStep.js';
import { LoginSuccess } from './LoginSuccess.js';
import { OperatorsGrid } from './OperatorsGrid.js';
import { PinKeypadStep } from './PinKeypadStep.js';
import './login.css';

type Step = 'hotel' | 'select' | 'pin' | 'success';

export function LoginPage() {
  const [step, setStep] = useState<Step>('hotel');
  const [hotel, setHotel] = useState<HotelOption | null>(null);
  const [selected, setSelected] = useState<Operator | null>(null);
  const [authedUser, setAuthedUser] = useState<AuthUser | null>(null);
  const setStoreUser = useAuth((s) => s.setUser);
  const queryClient = useQueryClient();

  // HM-387: handler success PIN — salva user nello store globale + invalida
  // cache (perche' altre query potrebbero leggere "user inattivo") + transita
  // a step success. La redirect al `from` avviene dentro LoginSuccess (HM-388).
  const handleLoginSuccess = async (user: AuthUser) => {
    setStoreUser(user);
    setAuthedUser(user);
    await queryClient.invalidateQueries();
    setStep('success');
  };

  return (
    <main className="login-root">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">HM</div>
          <div className="login-title">Hotel Manager</div>
          <div className="login-subtitle">Gestionale reception</div>
        </div>

        <div className={`login-card${step === 'success' ? ' success' : ''}`}>
          {step === 'hotel' && (
            <HotelSelectStep
              onSelect={(h) => {
                setHotel(h);
                setStep('select');
              }}
            />
          )}
          {step === 'select' && (
            <OperatorsGrid
              hotelId={hotel?.id}
              onBack={() => {
                setHotel(null);
                setStep('hotel');
              }}
              onSelect={(op) => {
                setSelected(op);
                setStep('pin');
              }}
            />
          )}
          {step === 'pin' && selected && (
            <PinKeypadStep
              operator={selected}
              hotelId={hotel?.id}
              onBack={() => {
                setSelected(null);
                setStep('select');
              }}
              onSuccess={handleLoginSuccess}
            />
          )}
          {step === 'success' && authedUser && <LoginSuccess user={authedUser} />}
        </div>

        <div className="login-footer">
          <span className="version">Hotel Manager v2.6</span> · La Marca Hotels
          {' · '}
          <a href="/login-admin">Accesso amministratore</a>
        </div>
      </div>
    </main>
  );
}
